import { useTaskViewMode } from "../useTaskViewMode";
import { TaskBoard } from "../TaskBoard";
import { useTasks, useLiveQuery, useStore, useServices } from "@hq/client-react";
import type { Task, TaskSort } from "@hq/contracts";
import { compareTasks, type TaskGroup, todayOf, todayTasks } from "@hq/core";
import { cn } from "@z/ui";
import { Sun } from "lucide-react";
import { useState } from "react";
import { formatDayHeading } from "../format";
import { QuickAdd } from "../QuickAdd";
import { TaskGroupList } from "../TaskGroupList";
import { useNow } from "../useNow";
import { useProjectIndex } from "../useProjectNames";
import { ViewHeader } from "../ViewHeader";
import { type ViewProps, viewClassName } from "./shared";

const OPEN = { status: ["open"] } as const;
const NONE: readonly Task[] = [];

/**
 * The home list: Overdue, Today (due today or scheduled today) and This Evening, composed by
 * `todayTasks` in core. Scheduled tasks show their time on the row.
 */
export function TodayView({ onOpenTask, now: nowOverride, className }: ViewProps) {
  const now = useNow(nowOverride);
  const { projects } = useProjectIndex();
  const { data } = useTasks(OPEN);
  const [mode, setMode] = useTaskViewMode("today");
  const [sort, setSort] = useState<TaskSort>({ by: "manual", direction: "asc" });

  const store = useStore();
  const services = useServices();
  const moveToGroup = async (task: Task, group: TaskGroup) => {
    const today = todayOf(now);
    // Overdue is a computed deadline bucket, not a manually assigned status.
    if (group.key === "overdue") return;
    if (task.dueDate && task.dueDate < today) await services.tasks.setDueDate(task.id, today);
    await services.tasks.plan(task.id, today);
    await services.tasks.setEvening(task.id, group.key === "evening");
    if (group.key === "priorities") await services.tasks.setPriority(task.id, "high");
    else if (group.key === "today" && task.priority === "high")
      await services.tasks.setPriority(task.id, null);
  };
  const { data: eveningCollapsed } = useLiveQuery(
    ["kv"],
    (s) => s.kv.get<boolean>("today.eveningCollapsed"),
    [],
  );
  const sections = todayTasks(data ?? NONE, now);
  const sorted = (tasks: Task[]) => [...tasks].sort((a, b) => compareTasks(a, b, sort));
  const groups: TaskGroup[] = [
    { key: "overdue", label: "Overdue", tasks: sorted(sections.overdue) },
    {
      key: "priorities",
      label: "Priorities",
      tasks: sorted(sections.today.filter((t) => t.priority === "high")),
    },
    {
      key: "today",
      label: "Today",
      tasks: sorted(sections.today.filter((t) => t.priority !== "high")),
    },
    { key: "evening", label: "This Evening", tasks: sorted(sections.evening) },
  ];
  const count = groups.reduce((n, g) => n + g.tasks.length, 0);

  return (
    <div data-slot="today-view" className={cn(viewClassName, className)}>
      <ViewHeader
        mode={mode}
        onModeChange={setMode}
        title={
          <span className="inline-flex items-center gap-2">
            <Sun className="size-5 text-warning" />
            Today
          </span>
        }
        count={count}
        displayControls={count > 0 && mode === "list"}
        sort={sort}
        onSortChange={setSort}
      >
        <span className="text-xs text-fg-muted">{formatDayHeading(todayOf(now), now)}</span>
      </ViewHeader>
      <p className="px-1 pb-4 text-sm text-fg-muted">
        {sections.today.filter((t) => t.priority === "high").length > 3
          ? "More than three priorities today. Consider moving a few to another day."
          : "Choose a few priorities. Use When to plan the rest for another day."}
      </p>
      {mode === "board" ? (
        <TaskBoard
          tasks={groups.flatMap((g) => g.tasks)}
          now={now}
          onOpenTask={onOpenTask}
          label="Today"
          quickAdd={<QuickAdd now={now} plannedDate={todayOf(now)} />}
        />
      ) : (
        <TaskGroupList
          reorderable={sort.by === "manual"}
          groups={groups}
          emptyGroupKeys={["priorities", "today", "evening"]}
          onMoveToGroup={moveToGroup}
          canMoveToGroup={(_task, group) => group.key !== "overdue"}
          collapsibleGroupKeys={["evening"]}
          collapsedGroupKeys={eveningCollapsed ? ["evening"] : []}
          onToggleGroup={() => void store.kv.set("today.eveningCollapsed", !eveningCollapsed)}
          now={now}
          projects={projects}
          quickAdd={
            <QuickAdd
              now={now}
              plannedDate={todayOf(now)}
              placeholder="Add a task for today… e.g. “Call Sam at 3pm”"
            />
          }
          emptyTitle={sections.evening.length ? "Your daytime is clear" : "A clear day"}
          emptyDescription="Add something you want to work on today. A work day does not create a deadline."
          aria-label="Today"
          {...(onOpenTask ? { onOpenTask } : {})}
        />
      )}
    </div>
  );
}
